import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { adminAPI } from '../../services/api';
import AsyncTypeahead from '../ui/AsyncTypeahead';
import Tabs from '../ui/Tabs';
import CreateInvoiceModal from '../CreateInvoiceModal';
import RecordInvoicesPanel from '../invoices/RecordInvoicesPanel';
import InvoiceDrawerHost from '../invoices/InvoiceDrawerHost';
import { AccountTypeaheadOption } from './AccountTypeahead';
import { CustomerTypeaheadOption } from '../customer/CustomerTypeahead';
import { Invoice } from '../../types/invoice';
import QuoteTermsEditor, {
  QuoteTermsShape,
  QuoteTermsSummary,
  compactQuoteTermsOverride,
  getDefaultQuoteTerms,
  mergeQuoteTerms,
} from '../quotes/QuoteTermsEditor';

export type AccountSummary = {
  id: string;
  name: string;
  customer_count?: number;
  commercial_terms?: QuoteTermsShape | null;
  created_at?: string;
  updated_at?: string;
};

type AccountDrawerTab = 'overview' | 'terms' | 'customers' | 'invoices' | 'payments';

type CustomerIdentityOption = {
  id: string;
  label: string;
  description?: string;
  email?: string;
  phone?: string | null;
  stripeCustomerId?: string | null;
  localCustomerId?: string | null;
};

type AccountDetailContentProps = {
  account: AccountSummary | null;
  onAccountUpdated?: () => void;
};

function formatCentsToDollars(value?: number | string | null) {
  const cents = Number(value || 0);
  return (Number.isFinite(cents) ? cents : 0) / 100;
}

function splitDisplayName(label: string) {
  const trimmed = label.trim();
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    return { firstName: trimmed || null, lastName: null };
  }

  return {
    firstName: parts[0] || null,
    lastName: parts.slice(1).join(' ') || null,
  };
}

export default function AccountDetailContent({ account, onAccountUpdated }: AccountDetailContentProps) {
  const queryClient = useQueryClient();
  const [drawerTab, setDrawerTab] = useState<AccountDrawerTab>('overview');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerIdentityOption | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [commercialTerms, setCommercialTerms] = useState<QuoteTermsShape | null>(null);
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [selectedFullInvoice, setSelectedFullInvoice] = useState<Partial<Invoice> | null>(null);

  useEffect(() => {
    setDrawerTab('overview');
    setSelectedCustomer(null);
    setShowArchived(false);
    setCommercialTerms(null);
  }, [account?.id]);

  const { data: accountDetails, isLoading: loadingAccount } = useQuery(
    ['account-drawer-account', account?.id],
    async () => {
      if (!account?.id) {
        return null;
      }
      const response = await adminAPI.getAccount(account.id);
      return response.data;
    },
    {
      enabled: Boolean(account?.id),
      staleTime: 60_000,
      retry: false,
    },
  );

  const { data: attachedCustomersPayload, isLoading: loadingCustomers, refetch: refetchCustomers } = useQuery(
    ['account-drawer-customers', account?.id],
    async () => {
      if (!account?.id) {
        return null;
      }
      const response = await adminAPI.getSingleAccountCustomers(account.id);
      return response.data;
    },
    {
      enabled: Boolean(account?.id),
      staleTime: 60_000,
      retry: false,
    },
  );

  const { data: quoteTermsPayload, isLoading: loadingQuoteTerms } = useQuery(
    'account-drawer-quote-terms',
    async () => {
      const response = await adminAPI.getQuoteTerms();
      return response.data;
    },
    {
      staleTime: 60_000,
      retry: false,
    },
  );

  const { data: accountTermsPayload, isLoading: loadingAccountTerms, refetch: refetchAccountTerms } = useQuery(
    ['account-drawer-terms', account?.id],
    async () => {
      if (!account?.id) {
        return null;
      }
      const response = await adminAPI.getAccountTerms(account.id);
      return response.data;
    },
    {
      enabled: Boolean(account?.id),
      staleTime: 60_000,
      retry: false,
      onSuccess: (payload) => {
        setCommercialTerms(payload?.commercial_terms || null);
      },
    },
  );

  const { data: invoicesPayload, isLoading: loadingInvoices, refetch: refetchInvoices } = useQuery(
    ['account-drawer-invoices', account?.id],
    async () => {
      if (!account?.id) {
        return null;
      }

      const response = await adminAPI.getAccountInvoices(account.id);
      return response.data;
    },
    {
      enabled: Boolean(account?.id),
      staleTime: 60_000,
      retry: false,
    },
  );

  const attachMutation = useMutation(
    async (customerOption: CustomerIdentityOption) => {
      if (!account?.id) {
        throw new Error('Account not selected');
      }

      const { firstName, lastName } = splitDisplayName(customerOption.label);
      if (customerOption.localCustomerId) {
        return adminAPI.addAccountContact(account.id, { customerId: customerOption.localCustomerId });
      }
      return adminAPI.assignCustomerAccountByIdentity({
        accountId: account.id,
        email: (customerOption.email || customerOption.description || '').trim().toLowerCase(),
        firstName,
        lastName,
        phone: customerOption.phone || null,
        stripeCustomerId: customerOption.stripeCustomerId || null,
        makeHome: false,
      });
    },
    {
      onSuccess: async () => {
        toast.success('Customer attached to account');
        setSelectedCustomer(null);
        await Promise.all([
          refetchCustomers(),
          refetchInvoices(),
          queryClient.invalidateQueries('admin-accounts'),
          queryClient.invalidateQueries(['account-drawer-account', account?.id]),
        ]);
        onAccountUpdated?.();
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Failed to attach customer');
      },
    },
  );

  const detachMutation = useMutation(
    async ({ contactId, status }: { contactId: string; status: 'active' | 'archived' }) => {
      if (!account?.id) {
        throw new Error('Account not selected');
      }
      return adminAPI.updateAccountContact(account.id, contactId, { status });
    },
    {
      onSuccess: async (_, { status }) => {
        toast.success(status === 'archived' ? 'Contact archived' : 'Contact restored');
        await Promise.all([
          refetchCustomers(),
          refetchInvoices(),
          queryClient.invalidateQueries('admin-accounts'),
          queryClient.invalidateQueries(['account-drawer-account', account?.id]),
        ]);
        onAccountUpdated?.();
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Failed to update contact');
      },
    },
  );

  const setPrimaryBillingMutation = useMutation(
    async (customerId: string) => {
      if (!account?.id) {
        throw new Error('Account not selected');
      }
      return adminAPI.setAccountPrimaryBillingContact(account.id, customerId);
    },
    {
      onSuccess: async () => {
        toast.success('Billing contact updated');
        await Promise.all([
          refetchCustomers(),
          queryClient.invalidateQueries(['account-drawer-account', account?.id]),
          queryClient.invalidateQueries('admin-accounts'),
        ]);
        onAccountUpdated?.();
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Failed to update billing contact');
      },
    },
  );

  const updateTermsMutation = useMutation(
    async (nextTerms: QuoteTermsShape | null) => {
      if (!account?.id) {
        throw new Error('Account not selected');
      }

      const tenantDefaults = mergeQuoteTerms(quoteTermsPayload?.quoteTerms || getDefaultQuoteTerms());
      return adminAPI.updateAccountTerms(
        account.id,
        nextTerms ? compactQuoteTermsOverride(nextTerms, tenantDefaults) : null,
      );
    },
    {
      onSuccess: async () => {
        toast.success('Account terms saved');
        await Promise.all([
          refetchAccountTerms(),
          queryClient.invalidateQueries(['account-drawer-account', account?.id]),
          queryClient.invalidateQueries('admin-accounts'),
        ]);
        onAccountUpdated?.();
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Failed to save account terms');
      },
    },
  );

  const attachedCustomers = useMemo(
    () => (Array.isArray(attachedCustomersPayload?.customers) ? attachedCustomersPayload.customers : []),
    [attachedCustomersPayload?.customers],
  );
  const visibleCustomers = showArchived
    ? [...attachedCustomers, ...(attachedCustomersPayload?.archived_customers || [])]
    : attachedCustomers;
  const invoices = useMemo(
    () => (Array.isArray(invoicesPayload?.invoices) ? invoicesPayload.invoices : []),
    [invoicesPayload?.invoices],
  );
  const paidInvoices = useMemo(
    () => invoices.filter((invoice: any) => {
      const status = typeof invoice?.status === 'string' ? invoice.status.toLowerCase() : '';
      return status === 'paid' || Number(invoice?.amount_paid || 0) > 0;
    }),
    [invoices],
  );

  const primaryBillingContact = useMemo(() => {
    return (
      attachedCustomers.find((entry: any) => entry?.is_primary_billing && entry?.account_contact_status !== 'archived') ||
      attachedCustomers.find((entry: any) => entry?.account_contact_status !== 'archived') ||
      attachedCustomers[0] ||
      null
    );
  }, [attachedCustomers]);

  const initialInvoiceCustomer = useMemo<CustomerTypeaheadOption | null>(() => {
    if (!primaryBillingContact) return null;
    const id = String(
      primaryBillingContact.customer_id ||
      primaryBillingContact.local_customer_id ||
      primaryBillingContact.customer_record_id ||
      primaryBillingContact.external_customer_id ||
      primaryBillingContact.stripe_customer_id ||
      primaryBillingContact.id ||
      '',
    );
    if (!id) return null;
    const email = primaryBillingContact.email || '';
    return {
      id,
      label: primaryBillingContact.display_name || primaryBillingContact.name || email || 'Billing contact',
      description: email || 'Account billing contact',
      email,
    };
  }, [primaryBillingContact]);

  const totalInvoiced = useMemo(
    () => invoices.reduce((sum: number, invoice: any) => sum + Number(invoice?.total || 0), 0),
    [invoices],
  );
  const totalCollected = useMemo(
    () => invoices.reduce((sum: number, invoice: any) => sum + Number(invoice?.amount_paid || 0), 0),
    [invoices],
  );

  const drawerLoading = loadingAccount || loadingCustomers || loadingInvoices;
  const termsLoading = loadingQuoteTerms || loadingAccountTerms;
  const drawerTitle = accountDetails?.name || account?.name || 'Account';
  const customerCount = Number(accountDetails?.customer_count ?? account?.customer_count ?? attachedCustomers.length ?? 0);
  const initialInvoiceAccount = useMemo<AccountTypeaheadOption | null>(() => {
    if (!account?.id) return null;
    return {
      id: account.id,
      label: drawerTitle,
      description: `${customerCount} customer${customerCount === 1 ? '' : 's'} attached`,
    };
  }, [account?.id, customerCount, drawerTitle]);
  const tenantQuoteTerms = useMemo(
    () => mergeQuoteTerms(quoteTermsPayload?.quoteTerms || getDefaultQuoteTerms()),
    [quoteTermsPayload?.quoteTerms],
  );
  const accountEffectiveTerms = useMemo(
    () => mergeQuoteTerms(commercialTerms || accountTermsPayload?.commercial_terms || null, tenantQuoteTerms),
    [accountTermsPayload?.commercial_terms, commercialTerms, tenantQuoteTerms],
  );
  const hasAccountOverride = Boolean(compactQuoteTermsOverride(commercialTerms || accountTermsPayload?.commercial_terms, tenantQuoteTerms));

  const loadCustomerOptions = async (query: string): Promise<CustomerIdentityOption[]> => {
    const response = await adminAPI.getCustomers({
      search: query || undefined,
      page: 1,
      limit: 10,
    });

    const customers = Array.isArray(response?.data?.customers) ? response.data.customers : [];
    return customers
      .filter((entry: any) => entry?.id)
      .map((entry: any) => ({
        id: String(entry.id),
        label: entry.name || entry.display_name || entry.email || 'Customer',
        description: entry.email || '',
        email: entry.email || '',
        phone: entry.phone || null,
        stripeCustomerId: entry.stripe_customer_id || null,
        localCustomerId: entry.local_customer_id || null,
      }));
  };

  if (!account) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="overflow-x-auto">
        <Tabs
          tabs={[
            { key: 'overview', label: 'Overview' },
            { key: 'terms', label: 'Terms' },
            { key: 'customers', label: 'Customers', count: customerCount },
            { key: 'invoices', label: 'Invoices', count: invoices.length },
            { key: 'payments', label: 'Payments' },
          ]}
          activeTab={drawerTab}
          onChange={(tab) => setDrawerTab(tab as AccountDrawerTab)}
          className="min-w-max"
        />
      </div>

      {drawerTab === 'overview' ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="card p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">Attached Customers</p>
            <p className="mt-2 text-3xl font-semibold text-on-surface">{customerCount}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">Invoices</p>
            <p className="mt-2 text-3xl font-semibold text-on-surface">{invoices.length}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">Collected</p>
            <p className="mt-2 text-3xl font-semibold text-on-surface">${formatCentsToDollars(totalCollected).toFixed(2)}</p>
          </div>
          <div className="card p-4 md:col-span-3">
            <h4 className="text-sm font-semibold text-on-surface">Account Details</h4>
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-on-surface-variant">Name</dt>
                <dd className="text-right text-on-surface">{drawerTitle}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-on-surface-variant">Created</dt>
                <dd className="text-right text-on-surface">{accountDetails?.created_at ? new Date(accountDetails.created_at).toLocaleString() : 'N/A'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-on-surface-variant">Updated</dt>
                <dd className="text-right text-on-surface">{accountDetails?.updated_at ? new Date(accountDetails.updated_at).toLocaleString() : 'N/A'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-on-surface-variant">Invoice Volume</dt>
                <dd className="text-right text-on-surface">${formatCentsToDollars(totalInvoiced).toFixed(2)}</dd>
              </div>
            </dl>
          </div>
        </div>
      ) : null}

      {drawerTab === 'terms' ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-on-surface">Commercial Terms</h4>
              <p className="mt-1 text-sm text-on-surface-variant">
                Account-level terms override tenant defaults when quotes are created for {drawerTitle}.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="btn-secondary"
                disabled={termsLoading || updateTermsMutation.isLoading || !hasAccountOverride}
                onClick={() => {
                  setCommercialTerms(null);
                  updateTermsMutation.mutate(null);
                }}
              >
                Reset to Defaults
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={termsLoading || updateTermsMutation.isLoading}
                onClick={() => updateTermsMutation.mutate(accountEffectiveTerms)}
              >
                {updateTermsMutation.isLoading ? 'Saving...' : 'Save Terms'}
              </button>
            </div>
          </div>

          {termsLoading ? (
            <div className="card p-4">
              <p className="text-sm text-on-surface-variant">Loading terms...</p>
            </div>
          ) : (
            <>
              <div className="rounded-card border border-outline/60 bg-surface-container-low p-3 text-sm text-on-surface-variant">
                {hasAccountOverride ? 'This account has custom quote terms.' : 'This account is inheriting tenant quote defaults.'}
              </div>
              <QuoteTermsSummary terms={accountEffectiveTerms} fallback={tenantQuoteTerms} />
              <div className="card p-4">
                <QuoteTermsEditor
                  value={accountEffectiveTerms}
                  fallback={tenantQuoteTerms}
                  inheritanceMode
                  onChange={setCommercialTerms}
                />
              </div>
            </>
          )}
        </div>
      ) : null}

      {drawerTab === 'customers' ? (
        <div className="space-y-4">
          <div className="card p-4">
            <h4 className="text-sm font-semibold text-on-surface">Attach Customer</h4>
            <p className="mt-1 text-sm text-on-surface-variant">Search tenant customers from Stripe and attach them to this account.</p>
            <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
              <div>
                <EntityCustomerTypeahead value={selectedCustomer} onChange={setSelectedCustomer} loadOptions={loadCustomerOptions} />
              </div>
              <button
                type="button"
                className="btn-primary"
                disabled={!selectedCustomer || attachMutation.isLoading}
                onClick={() => {
                  if (selectedCustomer) {
                    attachMutation.mutate(selectedCustomer);
                  }
                }}
              >
                {attachMutation.isLoading ? 'Attaching...' : 'Attach Customer'}
              </button>
            </div>
          </div>

          <div className="card p-4">
            <h4 className="text-sm font-semibold text-on-surface">Connected Customers</h4>
            <label className="mt-2 flex items-center gap-2 text-sm text-on-surface-variant">
              <input type="checkbox" checked={showArchived} onChange={(event) => setShowArchived(event.target.checked)} />
              Show archived contacts
            </label>
            {drawerLoading ? (
              <p className="mt-3 text-sm text-on-surface-variant">Loading customers...</p>
            ) : visibleCustomers.length === 0 ? (
              <p className="mt-3 text-sm text-on-surface-variant">No customers are attached to this account yet.</p>
            ) : (
              <div className="mt-3 space-y-3">
                {visibleCustomers.map((entry: any) => (
                  <div key={entry.account_contact_id || entry.id} className="flex items-start justify-between gap-3 rounded-card border border-ghost p-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-on-surface">{entry.display_name || entry.email}</p>
                        {entry.is_primary_billing ? (
                          <span className="rounded-full border border-success/30 bg-success/10 px-2 py-0.5 text-[11px] font-semibold text-success">
                            Billing contact
                          </span>
                        ) : null}
                        {entry.account_contact_status === 'archived' ? (
                          <span className="rounded-full border border-outline/60 bg-surface-container-low px-2 py-0.5 text-[11px] font-medium text-on-surface-variant">
                            Archived
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-1 text-xs text-on-surface-variant">{entry.email}</p>
                      {entry.external_customer_id ? (
                        <p className="mt-1 text-xs text-on-surface-variant">
                          External Customer ID: <span className="font-mono text-on-surface">{entry.external_customer_id}</span>
                        </p>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 flex-col gap-2">
                      {!entry.is_primary_billing && entry.account_contact_status !== 'archived' ? (
                        <button
                          type="button"
                          className="btn-secondary"
                          disabled={setPrimaryBillingMutation.isLoading}
                          onClick={() => setPrimaryBillingMutation.mutate(entry.id)}
                        >
                          Set Billing Contact
                        </button>
                      ) : null}
                      {entry.account_contact_status !== 'archived' ? (
                        <button
                          type="button"
                          className="btn-secondary"
                          disabled={detachMutation.isLoading || !entry.account_contact_id}
                          onClick={() => detachMutation.mutate({ contactId: entry.account_contact_id, status: 'archived' })}
                        >
                          Archive
                        </button>
                      ) : (
                        <button type="button" className="btn-secondary" disabled={detachMutation.isLoading || !entry.account_contact_id}
                          onClick={() => detachMutation.mutate({ contactId: entry.account_contact_id, status: 'active' })}>Restore</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {drawerTab === 'invoices' ? (
        <RecordInvoicesPanel
          contextType="account"
          invoices={invoices}
          loading={drawerLoading}
          emptyTitle="No invoices for this account yet"
          emptyDescription={
            attachedCustomers.length
              ? 'Create an invoice with this account and billing contact already selected.'
              : 'Attach or create a customer first, then create an invoice for this account.'
          }
          onCreateInvoice={() => setShowCreateInvoice(true)}
          onOpenInvoice={(invoice) => setSelectedFullInvoice(invoice)}
          onRefresh={() => {
            refetchInvoices();
            queryClient.invalidateQueries('admin-invoices');
          }}
        />
      ) : null}

      {drawerTab === 'payments' ? (
        <div className="card p-4">
          <h4 className="text-sm font-semibold text-on-surface">Payments</h4>
          {drawerLoading ? (
            <p className="mt-3 text-sm text-on-surface-variant">Loading payments...</p>
          ) : paidInvoices.length === 0 ? (
            <p className="mt-3 text-sm text-on-surface-variant">No payments found for this account.</p>
          ) : (
            <div className="mt-3 space-y-3">
              {paidInvoices.map((invoice: any) => (
                <div key={invoice.id} className="rounded-card border border-ghost p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-on-surface">{invoice.invoice_number || 'Invoice payment'}</p>
                      <p className="mt-1 text-xs text-on-surface-variant">Paid {invoice.paid_at ? new Date(invoice.paid_at).toLocaleString() : 'Date unavailable'}</p>
                    </div>
                    <p className="text-sm font-semibold text-green-700">${formatCentsToDollars(invoice.amount_paid).toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}
      {showCreateInvoice ? (
        <CreateInvoiceModal
          onClose={() => setShowCreateInvoice(false)}
          initialCustomer={initialInvoiceCustomer}
          initialAccount={initialInvoiceAccount}
          subtitle={
            initialInvoiceCustomer
              ? 'Create an invoice with this account and billing contact prefilled.'
              : 'Choose or create a customer before submitting this account invoice.'
          }
          onSuccess={(invoice) => {
            setShowCreateInvoice(false);
            refetchInvoices();
            queryClient.invalidateQueries('admin-invoices');
            if (invoice) {
              setSelectedFullInvoice(invoice);
            }
          }}
        />
      ) : null}
      <InvoiceDrawerHost
        open={Boolean(selectedFullInvoice)}
        invoice={selectedFullInvoice}
        onClose={() => setSelectedFullInvoice(null)}
        onInvoiceUpdated={() => {
          refetchInvoices();
          queryClient.invalidateQueries('admin-invoices');
        }}
      />
    </div>
  );
}

type EntityCustomerTypeaheadProps = {
  value: CustomerIdentityOption | null;
  onChange: (value: CustomerIdentityOption | null) => void;
  loadOptions: (query: string) => Promise<CustomerIdentityOption[]>;
};

function EntityCustomerTypeahead({ value, onChange, loadOptions }: EntityCustomerTypeaheadProps) {
  return (
    <AsyncTypeahead
      value={value}
      onChange={onChange}
      loadOptions={loadOptions}
      placeholder="Search customer by name or email..."
      emptyMessage="No customers matched your search."
    />
  );
}
